import { Form, Formik } from "formik";
import TextInput from "../UI/TextInput";
import { ReactSelect } from "../UI/Select";
import Checkbox from "../UI/Checkbox";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import { useAdminStripePrices } from "../../hooks/use-stripe-prices";
import { TypeAbonnement } from "../../utils/constants/account";
import moment from "moment/moment";
import { handleAPIError } from "../../utils/index.jsx";

const TYPE_ABONNEMENT_OPTIONS = [
  { value: TypeAbonnement.AUCUN, label: "Aucun" },
  { value: TypeAbonnement.TRIAL, label: "Trial" },
  { value: TypeAbonnement.EARLYBIRD, label: "Earlybird" },
  { value: TypeAbonnement.REGULAR, label: "Regular" },
  { value: TypeAbonnement.STANDARD, label: "Standard" },
  { value: TypeAbonnement.CUSTOM, label: "Custom" },
];

function SubscriptionForm({ compte, onSubmit }) {
  const { pricesByType, isLoading } = useAdminStripePrices();

  const initialValues = {
    type_abonnement: compte?.type_abonnement || TypeAbonnement.AUCUN,
    expiration_abonnement: compte?.expiration_abonnement
      ? moment(compte.expiration_abonnement).format("YYYY-MM-DD")
      : "",
    stripe_price_abonnement: compte?.stripe_price_abonnement || "",
    nb_biens_abonnement: compte?.nb_biens_abonnement ?? "",
    autorenew_abonnement: compte?.autorenew_abonnement || false,
    credits: compte?.credits != null ? (compte.credits / 100).toFixed(2) : "",
  };

  async function submitHandler(values) {
    try {
      await onSubmit(values);
    } catch (error) {
      handleAPIError(error);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={submitHandler}
    >
      {({ values, setFieldValue }) => {
        const availablePrices = pricesByType[values.type_abonnement] || [];

        return (
          <Form>
            <h2>Gérer l'abonnement</h2>
            <p>Regular : Ancien abonnement, 2 biens pour 4,9€</p>
            <p>Standard : Nouvel abonnement, 1 biens pour 4,9€</p>
            <p>
              Pour mettre quelqu'un en gratuit : Mettre en "Regular", sans date
              d'expiration
            </p>
            <ReactSelect
              name="type_abonnement"
              label="Type d'abonnement"
              className={"marginB10 marginT10"}
              onChange={() => {
                setFieldValue("stripe_price_abonnement", "");
              }}
              options={TYPE_ABONNEMENT_OPTIONS}
            />

            <ReactSelect
              name="stripe_price_abonnement"
              label="Prix Stripe"
              className={"marginB10"}
              disabled={isLoading || availablePrices.length === 0}
              options={availablePrices.map((price) => ({
                label: `${price.product_description} (${price.months} mois) ${
                  price.unit_amount != null &&
                  ` - ${(price.unit_amount / 100).toFixed(2)}€`
                }`,
                value: price.id,
              }))}
            />

            <TextInput
              name="expiration_abonnement"
              label="Date d'expiration"
              type="date"
            />

            <TextInput
              name="nb_biens_abonnement"
              label="Nombre de biens"
              type="number"
              min="0"
            />

            <Checkbox name="autorenew_abonnement" className="marginT10">
              Renouvellement automatique
            </Checkbox>

            <TextInput
              name="credits"
              label="Crédits"
              type="number"
              min="0"
              step="0.01"
            />

            <div className="text-right marginT10">
              <FormikSubmitButton>Enregistrer</FormikSubmitButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

function buildChangeDescription(compte, values) {
  const changes = [];
  if (compte?.type_abonnement !== values.type_abonnement) {
    changes.push(
      `Type: ${compte?.type_abonnement} → ${values.type_abonnement}`,
    );
  }
  const oldExpiration = compte?.expiration_abonnement
    ? moment(compte.expiration_abonnement).format("DD/MM/YYYY")
    : "N/A";
  const newExpiration = values.expiration_abonnement
    ? moment(values.expiration_abonnement).format("DD/MM/YYYY")
    : "N/A";
  if (oldExpiration !== newExpiration) {
    changes.push(`Expiration: ${oldExpiration} → ${newExpiration}`);
  }
  if (compte?.nb_biens_abonnement !== (values.nb_biens_abonnement || null)) {
    changes.push(
      `Nb biens: ${compte?.nb_biens_abonnement ?? "N/A"} → ${
        values.nb_biens_abonnement || "N/A"
      }`,
    );
  }
  if (compte?.autorenew_abonnement !== values.autorenew_abonnement) {
    changes.push(
      `Autorenew: ${compte?.autorenew_abonnement ? "Oui" : "Non"} → ${
        values.autorenew_abonnement ? "Oui" : "Non"
      }`,
    );
  }
  const oldCredits =
    compte?.credits != null ? (compte.credits / 100).toFixed(2) + "€" : "N/A";
  const newCredits = values.credits !== "" ? values.credits + "€" : "N/A";
  if (oldCredits !== newCredits) {
    changes.push(`Crédits: ${oldCredits} → ${newCredits}`);
  }
  return changes.length > 0 ? changes.join(", ") : null;
}

export default function ManageSubscriptionModal({
  compte,
  onSave,
  addCustomerCase,
  onClose,
  modal: ModalElement,
}) {
  async function handleSubmit(values) {
    const payload = {
      type_abonnement: values.type_abonnement,
      expiration_abonnement: values.expiration_abonnement || null,
      stripe_price_abonnement: values.stripe_price_abonnement || null,
      nb_biens_abonnement: values.nb_biens_abonnement
        ? parseInt(values.nb_biens_abonnement, 10)
        : null,
      autorenew_abonnement: values.autorenew_abonnement,
      credits:
        values.credits !== ""
          ? Math.round(parseFloat(values.credits) * 100)
          : null,
    };

    const changeDescription = buildChangeDescription(compte, values);

    await onSave(payload);

    if (changeDescription && addCustomerCase) {
      await addCustomerCase({
        id_compte: compte.id,
        commentaire: `Modification: ${changeDescription}`,
      });
    }
    onClose();
  }

  return (
    <ModalElement size="medium">
      <SubscriptionForm compte={compte} onSubmit={handleSubmit} />
    </ModalElement>
  );
}
